import { Button, Flex, Form, Input, Modal, Typography } from 'antd';
import { usePatchAdminAccessMessageMutation } from 'api/AdminAccessMessagesApi';
import { useSelector } from 'react-redux';
import { closeModal, selectModalData, selectModalName } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { logging } from 'utils/utils';
import './AddRecipientModal.css';

export const ADD_RECIPIENT_MODAL_NAME = 'ADD_RECIPIENT_MODAL_NAME';

export type AddRecipientModalProps = {
  adminAccessId: string;
  messageId: string;
  currentContacts: any[];
  onSuccess?: () => void;
};

function AddRecipientModal({ adminAccessId, messageId, currentContacts, onSuccess }: AddRecipientModalProps) {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [patchMessage, { isLoading: isPatching }] = usePatchAdminAccessMessageMutation();

  const handleCloseModal = () => {
    dispatch(closeModal());
    form.resetFields();
  };

  const handleAddRecipient = async () => {
    try {
      const newContact = await form.validateFields();
      const updatedContacts = [...currentContacts, newContact];

      await patchMessage({
        id: adminAccessId,
        messageId: messageId,
        contacts: updatedContacts
      }).unwrap();

      if (onSuccess) {
        onSuccess();
      }
      handleCloseModal();
    } catch (e) {
      logging(e);
    }
  };

  return (
    <Modal
      open={true}
      title={null}
      onCancel={handleCloseModal}
      footer={null}
      width={560}
      className="add-recipient-modal"
    >
      <div className="modal-header">
        <div className="modal-title">Add Recipient</div>
        <Typography.Text type="secondary">
          Add a new recipient to this message. They will receive the message upon delivery.
        </Typography.Text>
      </div>

      <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
        <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please input the name!' }]}>
          <Input placeholder="Full name" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please input the email!' },
            { type: 'email', message: 'The input is not valid E-mail!' }
          ]}
        >
          <Input placeholder="email@example.com" />
        </Form.Item>

        <Form.Item label="Phone" name="phone">
          <Input placeholder="+1 (555) 000-0000" />
        </Form.Item>

        <Flex justify="flex-end" gap={12} style={{ marginTop: 32 }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button type="primary" onClick={handleAddRecipient} loading={isPatching}>
            Add Recipient
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
}

function AddRecipientModalWrapper() {
  const modalName = useSelector(selectModalName);
  const modalData = useSelector(selectModalData);

  if (modalName !== ADD_RECIPIENT_MODAL_NAME) {
    return null;
  }

  return (
    <AddRecipientModal
      adminAccessId={modalData.adminAccessId}
      messageId={modalData.messageId}
      currentContacts={modalData.currentContacts}
      onSuccess={modalData.onSuccess}
    />
  );
}

export default AddRecipientModalWrapper;
